import { createClient } from '@supabase/supabase-js';
import { findDuplicateAnalysisName, findDuplicateDashboardName } from '../lib/reportingNames';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Storage key for session persistence
    storageKey: 'sb-auth-token',
    // Don't automatically refresh on window focus - this prevents SIGNED_IN events on tab switch
    // The session will still be valid, but we won't get unnecessary auth state change events
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
  global: {
    // Don't automatically refresh token on window focus
    headers: {
      'x-client-info': 'sports-card-dashboard',
    },
  },
});

export const DEACTIVATED_ACCOUNT_MESSAGE =
  'Sorry, you are unable to login at this time. Please contact support@pinkdolphincards.com for assistance.';
export const AUTH_BLOCKED_MESSAGE_KEY = 'auth_blocked_message';
export const PRESERVE_PASSWORD_GATE_KEY = 'preserve_password_gate';
export const EMAIL_CONFIRMATION_REQUIRED_MESSAGE =
  'Please confirm your email before signing in. Check your inbox for the confirmation link.';

function getAuthCallbackUrl() {
  const configuredSiteUrl = process.env.REACT_APP_SITE_URL?.trim();
  const baseUrl =
    configuredSiteUrl && configuredSiteUrl.length > 0
      ? configuredSiteUrl
      : (typeof window !== 'undefined' ? window.location.origin : '');

  return new URL('/auth/callback', baseUrl).toString();
}

// User service for authentication and schema management
export class UserService {
  private async getUserAccountStatus(email: string): Promise<{ exists: boolean; isActive: boolean }> {
    const normalizedEmail = email.trim().toLowerCase();
    const { data, error } = await supabase
      .from('users')
      .select('is_active')
      .ilike('email', normalizedEmail)
      .limit(1)
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      return { exists: false, isActive: false };
    }

    return { exists: true, isActive: data.is_active !== false };
  }

  async signUp(email: string, password: string) {
    try {
      const now = new Date();
      const trialEndsAt = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: getAuthCallbackUrl(),
          data: {
            password_set: true,
            trial_started_at: now.toISOString(),
            trial_ends_at: trialEndsAt.toISOString(),
            has_payment_method: false,
          },
        },
      });

      if (error) throw error;

      // Note: User schema will be created automatically on first data upload
      // This eliminates the need for separate schema creation during signup

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  }

  async resendSignupConfirmation(email: string) {
    try {
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: getAuthCallbackUrl(),
        },
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Resend signup confirmation error:', error);
      return { data: null, error };
    }
  }

  async signIn(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        const errorMessage = error.message?.toLowerCase() || '';
        if (errorMessage.includes('email not confirmed')) {
          throw new Error(EMAIL_CONFIRMATION_REQUIRED_MESSAGE);
        }
        throw error;
      }

      const signedInEmail = data.user?.email?.toLowerCase();
      if (signedInEmail) {
        const accountStatus = await this.getUserAccountStatus(signedInEmail);
        // Allow first-time sign-ins before the app has created public.users records.
        if (accountStatus.exists && !accountStatus.isActive) {
          if (typeof window !== 'undefined') {
            sessionStorage.setItem(AUTH_BLOCKED_MESSAGE_KEY, DEACTIVATED_ACCOUNT_MESSAGE);
            sessionStorage.setItem(PRESERVE_PASSWORD_GATE_KEY, 'true');
          }
          await supabase.auth.signOut();
          throw new Error(DEACTIVATED_ACCOUNT_MESSAGE);
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  }

  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  }

  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      return { user, error: null };
    } catch (error) {
      console.error('Get current user error:', error);
      return { user: null, error };
    }
  }

  async isUserActive(email: string): Promise<boolean> {
    try {
      const accountStatus = await this.getUserAccountStatus(email);
      // Missing users row is expected for newly verified accounts on first login.
      return accountStatus.exists ? accountStatus.isActive : true;
    } catch (error) {
      console.error('isUserActive check error:', error);
      return false;
    }
  }

  async getUserSchema(email: string) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('user_schema_name')
        .eq('email', email)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return { schemaName: data?.user_schema_name, error: null };
    } catch (error) {
      console.error('Get user schema error:', error);
      return { schemaName: null, error };
    }
  }

  // Profile management methods
  async updateUserEmail(newEmail: string) {
    try {
      const { data, error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update email error:', error);
      return { data: null, error };
    }
  }

  async updateUserPassword(newPassword: string) {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update password error:', error);
      return { data: null, error };
    }
  }

  async updateUserProfile(updates: { email?: string; password?: string }) {
    try {
      const { data, error } = await supabase.auth.updateUser(updates);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update profile error:', error);
      return { data: null, error };
    }
  }

  private async createUserSchema(email: string) {
    try {
      const { data, error } = await supabase.rpc('create_user_schema', {
        user_email: email
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create user schema error:', error);
      return { data: null, error };
    }
  }
}

// Data service for user-specific operations using SQL functions
export class DataService {
  public userEmail: string;
  private userSchema: string | null = null;

  constructor(userEmail: string) {
    this.userEmail = userEmail;
  }

  // Ensure user schema and tables exist (call this on first login)
  async ensureUserSchemaExists(): Promise<void> {
    try {
      console.log('🔧 Ensuring user schema exists for:', this.userEmail);
      
      if (!this.userEmail) {
        throw new Error('User email is required to ensure schema exists');
      }
      
      const { data, error } = await supabase.rpc('ensure_user_schema_exists', {
        user_email: this.userEmail
      });

      if (error) {
        console.error('❌ RPC error ensuring user schema:', error);
        throw new Error(`Failed to ensure user schema: ${error.message || error}`);
      }

      if (data && data.success) {
        console.log('✅ User schema ensured successfully:', {
          user_schema: data.user_schema,
          schema_created: data.schema_created,
          tables_created: data.tables_created,
          user_created: data.user_created
        });
        
        // Cache the schema name
        if (data.user_schema) {
          this.userSchema = data.user_schema;
        }
      } else {
        const errorMsg = data?.message || 'Unknown error';
        console.error('❌ Schema ensure returned failure:', data);
        throw new Error(`Failed to ensure user schema: ${errorMsg}`);
      }
    } catch (error: any) {
      console.error('❌ Error ensuring user schema exists:', error);
      // Re-throw with more context
      throw new Error(`Error ensuring user schema for ${this.userEmail}: ${error.message || error}`);
    }
  }

  // Get the user's schema name from the database (cached)
  private async getUserSchemaName(): Promise<string> {
    if (this.userSchema) {
      return this.userSchema;
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('user_schema_name')
        .eq('email', this.userEmail)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      
      this.userSchema = data?.user_schema_name;
      if (!this.userSchema) {
        throw new Error('User schema not found');
      }
      
      return this.userSchema;
    } catch (error) {
      console.error('Error getting user schema:', error);
      throw new Error('Failed to get user schema');
    }
  }

  // Test function to debug RPC calls
  async testRpcFunction(transactionId: string) {
    console.log('🧪 Testing RPC function directly...');
    console.log('User email:', this.userEmail);
    console.log('Transaction ID:', transactionId);
    
    // Test with minimal data
    const testUpdates = { status: 'sold' };
    const testJson = JSON.stringify(testUpdates);
    
    console.log('Test updates object:', testUpdates);
    console.log('Test JSON string:', testJson);
    console.log('JSON valid:', JSON.parse(testJson));
    
    // Test 1: Send as JSON string
    console.log('🧪 Test 1: Sending as JSON string');
    try {
      const { data: data1, error: error1 } = await supabase.rpc('update_user_transaction', {
        user_email: this.userEmail,
        transaction_id: transactionId,
        updates: testJson
      });
      
      console.log('Test 1 - JSON string response:', { data: data1, error: error1 });
    } catch (err) {
      console.error('Test 1 - JSON string error:', err);
    }
    
    // Test 2: Send as object directly
    console.log('🧪 Test 2: Sending as object directly');
    try {
      const { data: data2, error: error2 } = await supabase.rpc('update_user_transaction', {
        user_email: this.userEmail,
        transaction_id: transactionId,
        updates: testUpdates
      });
      
      console.log('Test 2 - Object response:', { data: data2, error: error2 });
      return { data: data2, error: error2 };
    } catch (err) {
      console.error('Test 2 - Object error:', err);
      throw err;
    }
  }

  // Get transactions for the user using SQL function
  async getTransactions() {
    try {
      const { data, error } = await supabase.rpc('get_user_transactions', {
        user_email: this.userEmail
      });

      if (error) throw error;
      
      // The function returns the raw array of transactions directly
      if (Array.isArray(data)) {
        return data;
      } else {
        console.warn('Unexpected transactions response format:', data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  // Insert transactions using SQL function
  async insertTransactions(transactions: any[]) {
    try {
      console.log('Inserting transactions to user:', this.userEmail);
      console.log('Transaction data:', transactions);

      // Call the function with parameter names matching the database function
      const { data, error } = await supabase.rpc('insert_user_transactions', {
        user_email: this.userEmail,
        transaction_data: transactions
      });

      if (error) throw error;

      console.log('Transactions inserted successfully:', data);
      console.log('Full response with debug info:', JSON.stringify(data, null, 2));
      return data;
    } catch (error) {
      console.error('Error inserting transactions:', error);
      throw error;
    }
  }

  // Get grading orders using SQL function
  async getGradingOrders() {
    try {
      const { data, error } = await supabase.rpc('get_user_grading_orders', {
        user_email: this.userEmail
      });

      if (error) throw error;

      // The function returns the raw array of grading orders directly
      if (Array.isArray(data)) {
        return data;
      } else {
        console.warn('Unexpected grading orders response format:', data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching grading orders:', error);
      throw error;
    }
  }

  // Insert grading orders using SQL function
  async insertGradingOrders(gradingOrders: any[]) {
    try {
      console.log('Inserting grading orders to user:', this.userEmail);
      console.log('Grading order data:', gradingOrders);

      const { data, error } = await supabase.rpc('insert_user_grading_orders', {
        user_email: this.userEmail,
        grading_order_data: gradingOrders
      });

      if (error) {
        console.error('Supabase RPC error:', error);
        throw new Error(`Database error: ${error.message} (Code: ${error.code})`);
      }

      // The function returns an array with one result object
      const result = Array.isArray(data) && data.length > 0 ? data[0] : data;

      if (result && !result.success) {
        const errorDetails = result.error_messages ? result.error_messages.join('; ') : 'Unknown error';
        throw new Error(`Failed to insert grading orders: ${result.message || 'Unknown error'} - Details: ${errorDetails}`);
      }

      console.log('Grading orders inserted successfully:', data);
      return data;
    } catch (error) {
      console.error('Error inserting grading orders:', error);
      throw error;
    }
  }

  // Get expenses using SQL function
  async getExpenses() {
    try {
      const { data, error } = await supabase.rpc('get_user_expenses', {
        user_email: this.userEmail
      });

      if (error) throw error;

      // The function returns the raw array of expenses directly
      if (Array.isArray(data)) {
        return data;
      } else {
        console.warn('Unexpected expenses response format:', data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      throw error;
    }
  }

  // Insert expenses using SQL function
  async insertExpenses(expenses: any[]) {
    try {
      console.log('Inserting expenses to user:', this.userEmail);
      console.log('Expense data:', expenses);

      const { data, error } = await supabase.rpc('insert_user_expenses', {
        user_email: this.userEmail,
        expense_data: expenses
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to insert expenses');
      }

      console.log('Expenses inserted successfully:', data);
      return data;
    } catch (error) {
      console.error('Error inserting expenses:', error);
      throw error;
    }
  }

  // Get saved analyses (using public schema with user_id)
  async getSavedAnalyses() {
    try {
      const { data, error } = await supabase.rpc('get_user_saved_analyses', {
        user_email: this.userEmail
      });

      if (error) throw error;
      
      // Convert snake_case to camelCase for frontend
      return (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        dataSource: item.data_source,
        metrics: item.metrics || [],
        groupBy: item.group_by || [],
        chartType: item.chart_type,
        filters: item.filters || {},
        timeRange: item.time_range,
        timeGranularity: item.time_granularity,
        description: item.description || '',
        createdAt: item.created_at,
        lastModified: item.last_modified
      }));
    } catch (error) {
      console.error('Error fetching saved analyses:', error);
      throw error;
    }
  }

  // Data Connections methods
  async getDataConnections() {
    try {
      const { data, error } = await supabase.rpc('get_user_data_connections', {
        user_email: this.userEmail
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching data connections:', error);
      throw error;
    }
  }

  async saveDataConnection(
    connectionName: string,
    dataType: string,
    columnMapping: Record<string, string>
  ) {
    try {
      console.log('DataService.saveDataConnection called with:', {
        userEmail: this.userEmail,
        connectionName,
        dataType,
        columnMapping
      });

      const { data, error } = await supabase.rpc('save_user_data_connection', {
        user_email: this.userEmail,
        connection_name: connectionName,
        data_type: dataType,
        column_mapping: columnMapping
      });

      console.log('save_user_data_connection response:', { data, error });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving data connection:', error);
      throw error;
    }
  }

  async updateDataConnectionStats(connectionId: string, recordCount: number) {
    try {
      const { data, error } = await supabase.rpc('update_data_connection_stats', {
        user_email: this.userEmail,
        connection_id: connectionId,
        record_count: recordCount
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating data connection stats:', error);
      throw error;
    }
  }

  async deleteDataConnection(connectionId: string) {
    try {
      console.log('DataService.deleteDataConnection called with:', {
        userEmail: this.userEmail,
        connectionId,
        connectionIdType: typeof connectionId
      });

      const { data, error } = await supabase.rpc('delete_connection_and_data', {
        user_email: this.userEmail,
        connection_id: connectionId
      });

      console.log('Supabase RPC response:', { data, error });

      if (error) throw error;
      
      if (data && !data.success) {
        throw new Error(data.message || 'Failed to delete data connection and data');
      }

      console.log('Data connection and associated data deleted successfully:', data);
      return data;
    } catch (error) {
      console.error('Error deleting data connection and data:', error);
      throw error;
    }
  }

  /** Throws if another saved analysis already uses this name (per user, case-insensitive). */
  async assertUniqueAnalysisName(name: string, excludeAnalysisId?: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error("Analysis name is required");
    }
    const analyses = await this.getSavedAnalyses();
    const existing = findDuplicateAnalysisName(analyses, trimmed, excludeAnalysisId);
    if (existing) {
      throw new Error(`You already have an analysis named "${trimmed}". Choose a different name.`);
    }
  }

  /** Throws if another dashboard already uses this name (per user, case-insensitive). */
  async assertUniqueDashboardName(name: string, excludeDashboardId?: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error("Dashboard name is required");
    }
    const dashboards = await this.getDashboards();
    const existing = findDuplicateDashboardName(dashboards, trimmed, excludeDashboardId);
    if (existing) {
      throw new Error(`You already have a dashboard named "${trimmed}". Choose a different name.`);
    }
  }

  // Save analysis (using user schema via RPC)
  async saveAnalysis(analysis: any) {
    try {
      await this.assertUniqueAnalysisName(analysis.name);
      // Convert camelCase to snake_case for database columns
      const dbData = {
        id: analysis.id,
        name: analysis.name,
        data_source: analysis.dataSource || analysis.data_source,
        metrics: analysis.metrics || [],
        group_by: analysis.groupBy || analysis.group_by || [],
        chart_type: analysis.chartType || analysis.chart_type,
        filters: analysis.filters || {},
        time_range: analysis.timeRange || analysis.time_range,
        time_granularity: analysis.timeGranularity || analysis.time_granularity,
        description: analysis.description || '',
        created_at: analysis.createdAt || analysis.created_at || new Date().toISOString(),
        last_modified: analysis.lastModified || analysis.last_modified || new Date().toISOString()
      };

      const { data, error } = await supabase.rpc('save_user_analysis', {
        user_email: this.userEmail,
        analysis_data: dbData
      });

      if (error) throw error;
      
      if (data && !data.success) {
        throw new Error(data.message || 'Failed to save analysis');
      }
      
      return data;
    } catch (error) {
      console.error('Error saving analysis:', error);
      throw error;
    }
  }

  // Get dashboards (using user schema via RPC)
  async getDashboards() {
    try {
      const { data, error } = await supabase.rpc('get_user_dashboards', {
        user_email: this.userEmail
      });

      if (error) throw error;
      
      // Convert snake_case to camelCase for frontend
      return (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        analyses: item.analyses || [],
        createdAt: item.created_at,
        updatedAt: item.updated_at
      }));
    } catch (error) {
      console.error('Error fetching dashboards:', error);
      throw error;
    }
  }

  // Save dashboard (using user schema via RPC)
  async saveDashboard(dashboard: any) {
    try {
      await this.assertUniqueDashboardName(dashboard.name);
      // Convert camelCase to snake_case for database columns
      const dbData = {
        id: dashboard.id,
        name: dashboard.name,
        analyses: dashboard.analyses || [],
        created_at: dashboard.createdAt || dashboard.created_at || new Date().toISOString(),
        updated_at: dashboard.updatedAt || dashboard.updated_at || new Date().toISOString()
      };

      const { data, error } = await supabase.rpc('save_user_dashboard', {
        user_email: this.userEmail,
        dashboard_data: dbData
      });

      if (error) throw error;
      
      if (data && !data.success) {
        throw new Error(data.message || 'Failed to save dashboard');
      }
      
      return data;
    } catch (error) {
      console.error('Error saving dashboard:', error);
      throw error;
    }
  }

  // Delete saved analysis (using user schema via RPC)
  async deleteSavedAnalysis(analysisId: string) {
    try {
      const { data, error } = await supabase.rpc('delete_user_analysis', {
        user_email: this.userEmail,
        analysis_id: analysisId
      });

      if (error) throw error;
      
      if (data && !data.success) {
        throw new Error(data.message || 'Failed to delete analysis');
      }
    } catch (error) {
      console.error('Error deleting saved analysis:', error);
      throw error;
    }
  }

  // Delete dashboard (using user schema via RPC)
  async deleteDashboard(dashboardId: string) {
    try {
      const { data, error } = await supabase.rpc('delete_user_dashboard', {
        user_email: this.userEmail,
        dashboard_id: dashboardId
      });

      if (error) throw error;
      
      if (data && !data.success) {
        throw new Error(data.message || 'Failed to delete dashboard');
      }
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      throw error;
    }
  }

  // Update saved analysis (using user schema via RPC)
  async updateSavedAnalysis(analysisId: string, updates: any) {
    try {
      // Convert camelCase to snake_case for database columns
      const dbUpdates: any = {};

      if (updates.name !== undefined) {
        await this.assertUniqueAnalysisName(updates.name, analysisId);
        dbUpdates.name = updates.name;
      }
      if (updates.dataSource !== undefined) dbUpdates.data_source = updates.dataSource;
      if (updates.metrics !== undefined) dbUpdates.metrics = updates.metrics;
      if (updates.groupBy !== undefined) dbUpdates.group_by = updates.groupBy;
      if (updates.chartType !== undefined) dbUpdates.chart_type = updates.chartType;
      if (updates.filters !== undefined) dbUpdates.filters = updates.filters;
      if (updates.timeRange !== undefined) dbUpdates.time_range = updates.timeRange;
      if (updates.timeGranularity !== undefined) dbUpdates.time_granularity = updates.timeGranularity;
      if (updates.description !== undefined) dbUpdates.description = updates.description;

      const { data, error } = await supabase.rpc('update_user_analysis', {
        user_email: this.userEmail,
        analysis_id: analysisId,
        updates: dbUpdates
      });

      if (error) throw error;
      
      if (data && !data.success) {
        throw new Error(data.message || 'Failed to update analysis');
      }
      
      return data;
    } catch (error) {
      console.error('Error updating saved analysis:', error);
      throw error;
    }
  }

  // Update dashboard (using user schema via RPC)
  async updateDashboard(dashboardId: string, updates: any) {
    try {
      // Convert camelCase to snake_case for database columns
      const dbUpdates: any = {};

      if (updates.name !== undefined) {
        await this.assertUniqueDashboardName(updates.name, dashboardId);
        dbUpdates.name = updates.name;
      }
      if (updates.analyses !== undefined) dbUpdates.analyses = updates.analyses;

      const { data, error } = await supabase.rpc('update_user_dashboard', {
        user_email: this.userEmail,
        dashboard_id: dashboardId,
        updates: dbUpdates
      });

      if (error) throw error;
      
      if (data && !data.success) {
        throw new Error(data.message || 'Failed to update dashboard');
      }
      
      return data;
    } catch (error) {
      console.error('Error updating dashboard:', error);
      throw error;
    }
  }

  // CRUD operations for interactive inventory management
  
  // Add a new transaction
  async addTransaction(transactionData: any) {
    try {
      console.log('Adding transaction for user:', this.userEmail);
      console.log('Transaction data:', transactionData);

      const { data, error } = await supabase.rpc('add_user_transaction', {
        user_email: this.userEmail,
        transaction_data: transactionData
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to add transaction');
      }

      console.log('Transaction added successfully:', data);
      return data;
    } catch (error) {
      console.error('Error adding transaction:', error);
      throw error;
    }
  }

  // Update an existing transaction
  async updateTransaction(transactionId: string, updates: any) {
    try {
      console.log('Updating transaction:', transactionId);
      console.log('Updates:', updates);

      // Use RPC function with object directly (not stringified)
      const { data, error } = await supabase.rpc('update_user_transaction', {
        user_email: this.userEmail,
        transaction_id: transactionId,
        updates: updates
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to update transaction');
      }

      console.log('Transaction updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Error updating transaction:', error);
      throw error;
    }
  }

  // Delete a transaction
  async deleteTransaction(transactionId: string) {
    try {
      console.log('Deleting transaction:', transactionId);

      const { data, error } = await supabase.rpc('delete_user_transaction', {
        user_email: this.userEmail,
        transaction_id: transactionId
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to delete transaction');
      }

      console.log('Transaction deleted successfully:', data);
      return data;
    } catch (error) {
      console.error('Error deleting transaction:', error);
      throw error;
    }
  }

  // Update transaction status (purchased -> at_grading -> sold)
  async updateTransactionStatus(transactionId: string, status: 'purchased' | 'at_grading' | 'sold') {
    try {
      console.log('Updating transaction status:', transactionId, 'to', status);
      
      const updatesObject = { status };
      const updatesString = JSON.stringify(updatesObject);
      
      console.log('Updates object:', updatesObject);
      console.log('Updates JSON string:', updatesString);
      console.log('User email:', this.userEmail);
      console.log('Transaction ID:', transactionId);
      
      // Test JSON parsing to make sure it's valid
      try {
        JSON.parse(updatesString);
        console.log('✅ JSON string is valid');
      } catch (parseError) {
        console.error('❌ JSON string is invalid:', parseError);
        throw new Error('Invalid JSON generated');
      }
      
      // Use RPC function with object directly (not stringified)
      const { data, error } = await supabase.rpc('update_user_transaction', {
        user_email: this.userEmail,
        transaction_id: transactionId,
        updates: updatesObject
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to update transaction status');
      }

      console.log('Transaction status updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Error updating transaction status:', error);
      throw error;
    }
  }

  // CRUD operations for grading orders
  
  // Add a new grading order
  async addGradingOrder(gradingOrderData: any) {
    try {
      console.log('Adding grading order for user:', this.userEmail);
      console.log('Grading order data:', gradingOrderData);

      const { data, error } = await supabase.rpc('add_user_grading_order', {
        user_email: this.userEmail,
        grading_order_data: gradingOrderData
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to add grading order');
      }

      console.log('Grading order added successfully:', data);
      return data;
    } catch (error) {
      console.error('Error adding grading order:', error);
      throw error;
    }
  }

  // Update an existing grading order
  async updateGradingOrder(gradingOrderId: string, updates: any) {
    try {
      console.log('Updating grading order:', gradingOrderId);
      console.log('Updates:', updates);

      // Use RPC function with object directly (not stringified)
      const { data, error } = await supabase.rpc('update_user_grading_order', {
        user_email: this.userEmail,
        grading_order_id: gradingOrderId,
        updates: updates
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to update grading order');
      }

      console.log('Grading order updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Error updating grading order:', error);
      throw error;
    }
  }

  // Delete a grading order
  async deleteGradingOrder(gradingOrderId: string) {
    try {
      console.log('Deleting grading order:', gradingOrderId);

      const { data, error } = await supabase.rpc('delete_user_grading_order', {
        user_email: this.userEmail,
        grading_order_id: gradingOrderId
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to delete grading order');
      }

      console.log('Grading order deleted successfully:', data);
      return data;
    } catch (error) {
      console.error('Error deleting grading order:', error);
      throw error;
    }
  }

  // Get cards associated with a grading order
  async getGradingOrderCards(gradingOrderId: string) {
    try {
      console.log('Getting cards for grading order:', gradingOrderId);

      const { data, error } = await supabase.rpc('get_grading_order_cards', {
        user_email: this.userEmail,
        grading_order_id_param: gradingOrderId
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to get grading order cards');
      }

      console.log('Grading order cards retrieved successfully:', data);
      return data;
    } catch (error) {
      console.error('Error getting grading order cards:', error);
      throw error;
    }
  }

  // Debug function to see what's in the database
  async debugGradingOrderCards() {
    try {
      console.log('Debugging grading order cards for user:', this.userEmail);

      const { data, error } = await supabase.rpc('debug_grading_order_cards', {
        user_email: this.userEmail
      });

      if (error) throw error;

      console.log('Debug data:', data);
      return data;
    } catch (error) {
      console.error('Error debugging grading order cards:', error);
      throw error;
    }
  }

  // Debug function to see what's in the transactions table
  async debugTransactions() {
    try {
      console.log('Debugging transactions for user:', this.userEmail);

      const { data, error } = await supabase.rpc('debug_user_transactions', {
        user_email: this.userEmail
      });

      if (error) throw error;

      console.log('Transactions debug data:', data);
      return data;
    } catch (error) {
      console.error('Error debugging transactions:', error);
      throw error;
    }
  }

  // Expense CRUD operations
  async addExpense(expenseData: any) {
    try {
      console.log('Adding expense for user:', this.userEmail);
      console.log('Expense data:', expenseData);

      const { data, error } = await supabase.rpc('add_user_expense', {
        user_email: this.userEmail,
        expense_data: expenseData
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to add expense');
      }

      console.log('Expense added successfully:', data);
      return data;
    } catch (error) {
      console.error('Error adding expense:', error);
      throw error;
    }
  }

  async updateExpense(expenseId: string, updates: any) {
    try {
      console.log('Updating expense:', expenseId, 'for user:', this.userEmail);
      console.log('Updates:', updates);

      const { data, error } = await supabase.rpc('update_user_expense', {
        user_email: this.userEmail,
        expense_id: expenseId,
        updates: JSON.stringify(updates)
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to update expense');
      }

      console.log('Expense updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Error updating expense:', error);
      throw error;
    }
  }

  async deleteExpense(expenseId: string) {
    try {
      console.log('Deleting expense:', expenseId, 'for user:', this.userEmail);

      const { data, error } = await supabase.rpc('delete_user_expense', {
        user_email: this.userEmail,
        expense_id: expenseId
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to delete expense');
      }

      console.log('Expense deleted successfully:', data);
      return data;
    } catch (error) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  }

  /** Read a JSON preference from the user's schema (returns null if missing). */
  async getUserPreference(prefKey: string): Promise<unknown | null> {
    const { data, error } = await supabase.rpc('get_user_preference', {
      user_email: this.userEmail,
      pref_key: prefKey,
    });
    if (error) throw error;
    return data ?? null;
  }

  /** Upsert a JSON preference in the user's schema. */
  async setUserPreference(prefKey: string, prefValue: unknown): Promise<void> {
    const { error } = await supabase.rpc('set_user_preference', {
      user_email: this.userEmail,
      pref_key: prefKey,
      pref_value: prefValue,
    });
    if (error) throw error;
  }

  /**
   * Ensures the user has exactly one default Overview dashboard (id overview-default) with seeded analyses.
   * Idempotent — safe to call after dashboards are loaded.
   */
  async ensureDefaultDashboard(): Promise<{ created: boolean; dashboardId?: string }> {
    const { data, error } = await supabase.rpc('ensure_user_default_dashboard', {
      user_email: this.userEmail,
    });
    if (error) throw error;
    const result = data as { success?: boolean; created?: boolean; dashboard_id?: string } | null;
    if (!result?.success) {
      throw new Error((result as { message?: string })?.message || 'Failed to ensure default dashboard');
    }
    return {
      created: Boolean(result.created),
      dashboardId: result.dashboard_id ?? undefined,
    };
  }
}